import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

// PAYMENT_FLOW_MERGE_ALERT: Subscription fields come only from check-subscription edge fn.
// Do not merge trial/subscription branching here — keep docs/PAYMENT_FLOW.md in sync.

interface SubscriptionStatus {
  subscribed: boolean;
  isTrial: boolean;
  trialEnd: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  subscription: SubscriptionStatus;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  subscription: { subscribed: false, isTrial: false, trialEnd: null, subscriptionEnd: null, loading: true },
  checkSubscription: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: false,
    isTrial: false,
    trialEnd: null,
    subscriptionEnd: null,
    loading: true,
  });
  const prevPaidSubscription = useRef<boolean | null>(null);

  const checkSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      const isPaid = (data.subscribed ?? false) && !(data.is_trial ?? false);
      if (prevPaidSubscription.current !== null && !prevPaidSubscription.current && isPaid) {
        trackEvent("subscription_started");
      }
      prevPaidSubscription.current = isPaid;
      setSubscription({
        subscribed: data.subscribed ?? false,
        isTrial: data.is_trial ?? false,
        trialEnd: data.trial_end ?? null,
        subscriptionEnd: data.subscription_end ?? null,
        loading: false,
      });
    } catch {
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const resetAuthState = useCallback(() => {
    setSession(null);
    prevPaidSubscription.current = null;
    setSubscription({ subscribed: false, isTrial: false, trialEnd: null, subscriptionEnd: null, loading: false });
  }, []);

  const handleSessionError = useCallback(async (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error ?? "");
    if (
      message.includes("Invalid Refresh Token") ||
      message.includes("Refresh Token Not Found")
    ) {
      // Clear stale local auth state so the extension can recover cleanly.
      await supabase.auth.signOut();
      resetAuthState();
      return;
    }
    setSubscription(prev => ({ ...prev, loading: false }));
  }, [resetAuthState]);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
      if (session) {
        setTimeout(() => checkSubscription(), 0);
      } else {
        setSubscription({ subscribed: false, isTrial: false, trialEnd: null, subscriptionEnd: null, loading: false });
      }
    });

    let mounted = true;

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (!mounted) return;
        setLoading(false);
        if (error) {
          void handleSessionError(error);
          return;
        }
        setSession(session);
        if (session) checkSubscription();
        else setSubscription(prev => ({ ...prev, loading: false }));
      })
      .catch((err) => {
        if (!mounted) return;
        setLoading(false);
        void handleSessionError(err);
      });

    return () => {
      mounted = false;
      authSub.unsubscribe();
    };
  }, [checkSubscription, handleSessionError]);

  // Auto-refresh subscription every 60s
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, subscription, checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
